import { getMonth, getYear, isAfter } from 'date-fns';
import router from '@/router';
import store from '@/store';

import { getStripeKeys } from './index';

const { loadStripe } = require('@stripe/stripe-js');

async function makeApiRequest (path, method = 'GET', body = undefined) {
  const headers = {};

  const basePath = '/loisirs-reservation';

  if (typeof body === 'object' && Object.keys(body).length !== 0) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }

  const params = {
    method: method,
    headers: headers,
    body: body
  };

  let apiUrl = `${basePath}${path}`;

  if (store.state.impersonateData) {
    if (apiUrl.includes('?')) {
      apiUrl = `${apiUrl}&impersonateData=${encodeURI(store.state.impersonateData)}`;
    } else {
      apiUrl = `${apiUrl}?impersonateData=${encodeURI(store.state.impersonateData)}`;
    }
  }

  return fetch(apiUrl, params)
    .then(async response => {
      const res = await response.json();

      // Improve
      if (res && !res.loginUrl && !response.ok) {
        throw (res);
      }

      return res;
    })
    .then(response => {
      if (response && response.loginUrl) {
        store.commit('setLoading', true);
        store.commit('resetError');
        window.location.href = response.loginUrl;
        return response;
      }

      if (response.hasOwnProperty('graphQLErrors')) {
        // store.commit('setError', JSON.stringify(response, null, 4));
        throw (response);
      }

      if (response.networkError && response.networkError.result.message === 'Endpoint request timed out') {
        // store.commit('setError', JSON.stringify(response, null, 4));
        throw (response);
      }

      return response;
    }).catch(err => {
      if (err && typeof err === 'object' && err.loginUrl) {
        store.commit('setLoading', true);
        store.commit('resetError');
        window.location.href = err.loginUrl;
        return err;
      }
      console.log('Error making api request', err);
      store.commit('setError', JSON.stringify(err, null, 4));
      store.commit('setLoading', false);
      return err;
    });
}

// Get workspace Info
const getSiteDetail = async args => makeApiRequest(`/api/info/workspace/${args.workspace}`);

// Get Product Information
const getProduct = async args => makeApiRequest(`/api/info/product/${args.product}?apiSource=${store.state.siteInfo.apiSource}`);

/**
 * Session products for the classic (non-Amplify) path. Always returns an array so callers never branch on error objects.
 * @param {{ activities: string, sessions: string, start_time: string, end_time: string, workspace: string }} args
 * @returns {Promise<object[]>}
 */
const getSessionProducts = async args => {
  const res = await makeApiRequest(
    `/api/info/session/products/${args.activities}/${args.sessions}/${args.start_time}/${args.end_time}/${args.workspace}`
  );
  return Array.isArray(res) ? res : [];
};

// Validade promo code
const submitPromoCode = async args => makeApiRequest(`/api/users/promo-code/validate?workspace=${args.workspace}&promo_code=${args.promoCode}&product_uuid=${args.productUuid}&horanet_product_code=${args.horanetProductCode}`);

// Validade gymlib code
const submitGymlibCode = async ({ code, workspace, customerUuid, price, startTime, endTime, gymlibAmount, productUuid }) => {
  let url = `/api/users/gymlib/validate?code=${code}&customerUuid=${customerUuid}&workspace=${workspace}&price=${price}&startTime=${startTime}&endTime=${endTime}&productUuid=${productUuid}`;
  if (gymlibAmount) url += `&gymlibAmount=${gymlibAmount}`;
  return makeApiRequest(url);
};

// Get logged User
const getUser = async () => makeApiRequest('/api/users/user');

// Update Existing Customer
const updateCustomer = async args => makeApiRequest('/api/users/edit-customer', 'POST', args);

// Create a new participant
const createParticipant = async args => makeApiRequest('/api/users/participant', 'POST', args);

// Get the tarrif required documents and conditions
const getUserTariffDocuments = async args => makeApiRequest(`/api/users/required-documents/${args.workspace}`, 'POST', args);

// Upload one document required by the tarrif
const uploadRequiredDocuments = async args => makeApiRequest('/api/users/upload/files', 'POST', args);

// Get order by UUID
const getOrderByUuid = async order => makeApiRequest(`/api/info/order/${order}/${store.getters.workspace}`);

const submitShoppingCart = async ignoreExistingShoppingCart => {
  const { siteInfo } = store.state;
  const args = {
    cartItem: store.state.cartItem,
    user: store.state.user.uuid,
    offerInfo: store.state.offerInfo,
    product_uuid: store.state.product.uuid,
    ignoreExistingShoppingCart: ignoreExistingShoppingCart,
    isInternalSessions: siteInfo.is_internal_session,
    apiSource: siteInfo.apiSource,
    isInternalSession: siteInfo.is_internal_session
  };

  return makeApiRequest('/api/users/shopping-cart', 'POST', args);
};

const removeShoppingCart = async () => {
  const args = {
    workspace: store.getters.workspace,
    user: store.state.user.uuid
  };

  const res = await makeApiRequest('/api/users/shopping-cart', 'DELETE', args);
  if (res.removeShoppingCartV2) {
    return res.removeShoppingCartV2;
  }
  return null;
};

// Get sessions directly to Horanet
const getSessions = async ({ month, year, is_first_request }) => {
  const { user, product, cartItem, offerInfo, siteInfo } = store.state;

  const firstParticipant = cartItem.participants && cartItem.participants[0];
  const period = firstParticipant?.tariff?.period ?? null;

  let date = { month: month, year: year };

  // if period is after date use period date
  if (period && is_first_request && isAfter(new Date(period.validityStartDate), new Date(`${year}-${month}`))) {
    date = {
      month: getMonth(new Date(period.validityStartDate)) + 1,
      year: getYear(new Date(period.validityStartDate))
    };
  }
  // if (period && is_first_request) {
  //   date = {
  //     month: getMonth(new Date(period.validityStartDate)) + 1,
  //     year: getYear(new Date(period.validityStartDate))
  //   };
  // }

  let periodToSend = null;

  if (period) {
    periodToSend = { ...period };
    delete periodToSend.renewedWith;
    delete periodToSend.__typename;
  }

  const args = {
    user,
    workspace: product.workspace,
    horanet_product_reference: !siteInfo.is_internal_session ? offerInfo.horanet_product.horanet_product_reference : null,
    participants_number: cartItem.participants.length,
    date: date,
    is_first_request: is_first_request,
    product_uuid: product.uuid,
    participants: cartItem.participants.map(p => p.uuid),
    isInternalSession: siteInfo.is_internal_session,
    offer: offerInfo.uuid,
    period: periodToSend
  };

  return makeApiRequest('/api/users/sessions', 'POST', args);
};

// Generate Stripe Checkout Session
const generatePaymentSession = async args => {
  const { workspace } = store.getters;

  const siteInfo = await getSiteDetail({ workspace });

  if (siteInfo.maintenance) {
    store.commit('setLoading', false);
    store.commit('setError', 'Site en maintenance');
    return;
  }

  let { stripeSessionID } = store.state;
  if (!stripeSessionID) {
    const response = await makeApiRequest('/api/users/pay', 'POST', args);

    if (response.hasOwnProperty('graphQLErrors') || !response.generatePaymentSession.success) {
      store.commit('setLoading', false);

      const message = response && response.generatePaymentSession ? response.generatePaymentSession.message : null;

      switch (message) {
        case 3:
          store.commit('setErrorMessage', 'Notre site connaît actuellement une forte affluence. Nous vous invitons à renouveler votre demande dans une trentaine de minutes.');
          return;

        default:
          store.commit('setError', JSON.stringify(response, null, 4));
          return;
      }
    }

    if (response.generatePaymentSession && response.generatePaymentSession.success) {
      stripeSessionID = response.generatePaymentSession.data.sessionId;
      if (response.generatePaymentSession.data.isOffert || response.generatePaymentSession.data.paymentLink) {
        await router.push({ name: 'Confirmation', query: { paiement: 'sucess', order: response.generatePaymentSession.data.order_uuid } });
        return;
      }

      switch (response.generatePaymentSession.data.platform) {
        case 'stripe':
          stripeSessionID = response.generatePaymentSession.data.sessionId;
          store.commit('setStripeSessionID', stripeSessionID);
          break;
        case 'sharegroop':
          window.location.href = response.generatePaymentSession.data.redirectUrl;
          return;
        default:
          break;
      }
    }
  }

  if (stripeSessionID) {
    const stripeKeys = getStripeKeys(siteInfo.workspace);
    const stripe = await loadStripe(stripeKeys.publicKey, { stripeAccount: stripeKeys.accountId });

    const redirect = await stripe.redirectToCheckout({ sessionId: stripeSessionID });

    if (redirect.error) {
      store.commit('setLoading', false);
      store.commit('setError', JSON.stringify(redirect.error.message, null, 4));
    }
  }
};

const isImpersonateMode = () => makeApiRequest('/api/users/impersonate', 'GET');

const getParticipantInsurance = uuid => makeApiRequest(`/api/users/insurance/${uuid}?workspace=${store.getters.workspace}`, 'GET');

const getAllSessions = async () => {
  const { user, product, cartItem, offerInfo, siteInfo } = store.state;

  if (siteInfo.horanetSource === 'postgres') {
    return makeApiRequest('/api/amplify/sessions/horanet', 'POST', {
      user,
      workspaceTimezone: siteInfo.timezone,
      codeSiteComptage: siteInfo.code_site_comptage,
      calendarMonths: siteInfo.tda_settings.calendarMonths,
      productReference: offerInfo.horanet_product.horanet_product_reference,
      participantsNumber: cartItem.participants.length
    });
  }

  return makeApiRequest('/api/users/all-sessions', 'POST', {
    user,
    workspace: product.workspace,
    horanet_product_reference: offerInfo.horanet_product.horanet_product_reference,
    participants_number: cartItem.participants.length,
    isInternalSession: siteInfo.is_internal_session
  });
};

const getAvailableDates = async ({ date, workspace, codeSiteComptage, productReference, productUuid, isFirstRequest, participantsAmount }) => {
  const { siteInfo, cartItem, offerInfo } = store.state;

  const firstParticipant = cartItem.participants && cartItem.participants[0];
  const period = firstParticipant?.tariff?.period ?? null;

  const args = {
    date: date,
    workspace: workspace,
    productReference: productReference,
    productUuid: productUuid,
    calendarMonths: siteInfo.tda_settings.calendarMonths,
    codeSiteComptage: codeSiteComptage,
    isInternalSession: siteInfo.is_internal_session,
    period,
    participants: cartItem.participants.map(p => p.uuid),
    offerType: { inscription: offerInfo.detail.inscription, stage1D: offerInfo.detail.stage1D },
    isFirstRequest,
    timezone: siteInfo.timezone,
    participantsAmount,
    firstRecurringSession: offerInfo.firstRecurringSession
  };
  return makeApiRequest('/api/amplify/availableDates', 'POST', args);
};

const getSessionForDate = async ({ date, workspace, codeSiteComptage, productUuid }) => {
  const { siteInfo, productServices, offerInfo, cartItem } = store.state;

  const args = {
    date: date,
    workspace: workspace,
    codeSiteComptage: codeSiteComptage,
    productUuid: productUuid,
    isInternalSession: siteInfo.is_internal_session,
    productServices,
    offerType: { inscription: offerInfo.detail.inscription, stage1D: offerInfo.detail.stage1D },
    timezone: siteInfo.timezone,
    participantsAmount: cartItem.participants.length,
    firstRecurringSession: offerInfo.firstRecurringSession
  };
  return makeApiRequest('/api/amplify/sessions', 'POST', args);
};

const validateSessionReservation = async session => {
  const { siteInfo, cartItem, offerInfo } = store.state;

  const firstParticipant = cartItem.participants && cartItem.participants[0];
  const period = firstParticipant?.tariff?.period ?? null;

  const args = {
    sessionId: session.code,
    isInternalSession: siteInfo.is_internal_session,
    workspace: siteInfo.workspace,
    participants: cartItem.participants.map(p => p.uuid),
    inscription: offerInfo.detail.inscription,
    period,
    firstRecurringSession: offerInfo.firstRecurringSession
  };

  return makeApiRequest('/api/amplify/validate', 'POST', args);
};

/**
 * Session products (Amplify). Always returns the `products` array (possibly empty), never a raw error envelope.
 * @param {object} args
 * @returns {Promise<{ products: object[], message?: string }>}
 */
const getSessionProductsAmplify = async args => {
  const res = await makeApiRequest('/api/amplify/session/products', 'POST', args);
  return {
    products: Array.isArray(res?.products) ? res.products : [],
    message: res?.message
  };
};

const getServiceByReference = async reference => makeApiRequest(`/api/amplify/serviceByReference?reference=${reference}`);
const getSessionsByInterval = async args => makeApiRequest('/api/amplify/getSessionsByInterval', 'POST', args);
const getParticipantSubscriptionsInfo = async (customerUuid, workspace) => makeApiRequest(`/api/users/shareGroop/subscriptions?customerUuid=${customerUuid}&workspace=${workspace}`);
const getParticipantActiveSubscriptionsInfo = async workspace => makeApiRequest(`/api/users/subscriptions?workspace=${workspace}`);
const getSharegroopCustomerInfo = async (customerUuid, workspace) => makeApiRequest(`/api/users/sginfo?customerUuid=${customerUuid}&workspace=${workspace}`);
const getSessionById = async (session, isInternalSession) => makeApiRequest('/api/amplify/session', 'POST', { id: session, isInternalSession });
const getServicesProduct = async servicesId => makeApiRequest('/api/amplify/productService', 'POST', { servicesId });

const addCourtShoppingCart = async booking => makeApiRequest('/api/users/addCourtShoppingCart', 'POST', { booking });
const removeCourtShoppingCart = async () => makeApiRequest('/api/users/removeCourtShoppingCart', 'DELETE');

const getCustomerHorses = async uuid => {
  const { user } = store.state;

  if (user.uuid !== uuid && !user.contacts.some(contact => contact.uuid === uuid)) {
    return [];
  }

  const horses = await makeApiRequest(`/api/users/customer-horses?uuid=${uuid}&workspace=${store.getters.workspace}`);

  return horses || [];
};

const tokenizeSharegroopCard = async (customerUuid, workspace, successUrl) => makeApiRequest('/api/users/tokenize-card', 'POST', { customerUuid, workspace, successUrl });

const createCourtBooking = async body => {
  if (store.state.user?.courtShoppingCart) {
    await removeCourtShoppingCart();
  }
  return makeApiRequest('/api/users/createCourtBooking', 'POST', { body });
};

const getCustomerAmplifyCardContracts = async (sessionId, codeSiteComptage) => makeApiRequest('/api/amplify/customerCards', 'POST', { sessionId, codeSiteComptage });

// Express checkout (Coupe File) — dedicated endpoints with optional auth
const EXPRESS_CHECKOUT_BASE = '/loisirs-reservation/api/express-checkout';

const getExpressCheckoutUser = async () => {
  try {
    const response = await fetch(`${EXPRESS_CHECKOUT_BASE}/user`, { method: 'GET', headers: {} });
    return await response.json();
  } catch (_err) {
    return { success: false, data: null };
  }
};

const getExpressCheckoutWorkspace = async workspace => {
  try {
    const response = await fetch(`${EXPRESS_CHECKOUT_BASE}/workspace/${workspace}`);
    return await response.json();
  } catch (_err) {
    return null;
  }
};

const getExpressCheckoutProduct = async (productUuid, apiSource = '') => {
  try {
    const response = await fetch(`${EXPRESS_CHECKOUT_BASE}/product/${productUuid}?apiSource=${apiSource}`);
    return await response.json();
  } catch (_err) {
    return null;
  }
};

const getExpressCheckoutQrcodes = async () => {
  try {
    const response = await fetch(`${EXPRESS_CHECKOUT_BASE}/order-qrcodes`, { method: 'GET', credentials: 'same-origin' });
    return await response.json();
  } catch (_err) {
    return { success: false, items: [], isComplete: false };
  }
};

const cancelPreReservedSessions = async orderGenericUuid => makeApiRequest('/api/users/cancel-pre-reserved-sessions', 'POST', { orderGenericUuid });

const getRecurringSessions = async ({ startDate, endDate, workspace, recurringUuid }) => makeApiRequest('/api/amplify/recurring-sessions', 'POST', { startDate, endDate, workspace, recurringUuid });

const getCustomerAmplifySubscriptionContract = async () => makeApiRequest('/api/amplify/customerSubscriptionContract', 'POST', { codeSiteComptage: store.state.siteInfo.code_site_comptage });

const getCustomerAmplifyUnpaidBookings = async () => {
  const codeSiteComptage = encodeURIComponent(store.state.siteInfo?.code_site_comptage ?? '');

  return makeApiRequest(`/api/amplify/unpaidBookings?codeSiteComptage=${codeSiteComptage}`, 'GET');
};

/**
 * Unpaid invoices for a given order, each carrying the regularisation URL.
 * @param {string} orderUuid Generic order UUID
 * @returns {Promise<{ data?: object|object[], success?: boolean, message?: string }>}
 */
const getUnpaidInvoices = async orderUuid => makeApiRequest(`/api/users/unpaid-invoices?orderUuid=${encodeURIComponent(orderUuid)}`, 'GET');

export {
  getUnpaidInvoices,
  getCustomerAmplifyUnpaidBookings,
  getCustomerAmplifySubscriptionContract,
  getCustomerAmplifyCardContracts,
  createCourtBooking,
  tokenizeSharegroopCard,
  getServicesProduct,
  addCourtShoppingCart,
  removeCourtShoppingCart,
  getSessionById,
  getParticipantSubscriptionsInfo,
  getSessionsByInterval,
  getServiceByReference,
  getSessionProductsAmplify,
  validateSessionReservation,
  getSessionForDate,
  getAvailableDates,
  getParticipantInsurance,
  isImpersonateMode,
  getSiteDetail,
  getProduct,
  getSessionProducts,
  getUser,
  createParticipant,
  getUserTariffDocuments,
  uploadRequiredDocuments,
  getOrderByUuid,
  submitShoppingCart,
  removeShoppingCart,
  getSessions,
  generatePaymentSession,
  getAllSessions,
  submitPromoCode,
  updateCustomer,
  submitGymlibCode,
  getCustomerHorses,
  getExpressCheckoutUser,
  getExpressCheckoutWorkspace,
  getExpressCheckoutProduct,
  getExpressCheckoutQrcodes,
  cancelPreReservedSessions,
  getRecurringSessions,
  getSharegroopCustomerInfo,
  getParticipantActiveSubscriptionsInfo
};
